/**
 * 路由表 —— 09 章 §9.2.2 + 契约 §25 的扩充。
 *
 * **这份表是菜单树的唯一来源**（§9.2.4：菜单由路由推导，不单独维护一份）。
 * 加页面就在这里加一行，菜单自动出现。
 *
 * `meta.perms` 的取值**必须逐字等于后端 `app/core/permissions.py::Perm` 的值**。
 * 09 章正文里用的是首轮的短名（`dataset:write` 等），与实际 `Perm` 不同——
 * 该章 §9.2.1 已注明「冲突时以 04 章为准」。这里用的是实际值。
 * `tests/routes.spec.ts` 会拿后端 `/me` 的 perms 全集做交叉校验：
 * 出现一个不在全集里的 token 就是拼错了，那种拼错的症状是"菜单项永久不显示"。
 */

import type { RouteRecordRaw } from 'vue-router'

/** 路由 meta 的形状。`declare module` 在 index.ts 里做。 */
export interface MtpRouteMeta {
  /** 面包屑与标签名。 */
  title: string
  /** 需要的权限，**任一满足**即可。空/缺省 = 只需登录。 */
  perms?: string[]
  /** 是否出现在左侧菜单。 */
  menu?: boolean
  /** 二级菜单的组名。同名的会被归到一组，组的顺序由第一次出现的位置决定。 */
  group?: string
  /**
   * 图标名。必须是 `components/ui/icons.ts` 里 `REGISTRY` 的键（Lucide 的导出名）。
   * 写错不会报错，只会在开发期控制台留一条警告、那个图标空着。
   */
  icon?: string
  /** 是否 keepAlive 缓存。列表页开、详情页关（详情页要重新拉数据）。 */
  keepAlive?: boolean
  /** 面包屑挂靠的父路由 name。详情页用。 */
  parent?: string
  /** 不需要登录。只有登录页与错误页。 */
  publicRoute?: boolean
  /** 全屏工作区：不渲染平台侧栏/顶栏，页面自带返回入口与操作栏。目前只有画布页。 */
  fullscreen?: boolean
  /**
   * 网页标题由页面自己定，守卫不要覆盖它（台账 AG-159）。
   *
   * 守卫是**异步**设标题的（等品牌 store 就绪），会盖掉页面在 `onMounted` 里
   * 写的那一个。匿名分享页要的是「智能体名字」本身，不是「智能体对话 · 品牌」。
   */
  ownTitle?: boolean
}

/**
 * 菜单分组的**图标**。组名在这里定义一次，路由里只引用。
 *
 * ⚠️ **顺序不由这个数组决定**——菜单顺序 = 路由表里的书写顺序
 * （`buildTree` 让每个分组占住它第一个成员出现的位置）。
 * 想调菜单顺序就调下面 `routes` 的顺序，不要动这个数组。
 * 两处都能定顺序的话必然漂移。
 */
export const MENU_GROUPS: { name: string; icon: string }[] = [
  { name: '数据管理', icon: 'FolderOpen' },
  { name: '模型管理', icon: 'Box' },
  { name: '训练任务', icon: 'Cpu' },
  { name: '评测分析', icon: 'ChartLine' },
  { name: '部署与服务', icon: 'Network' },
  { name: '监控', icon: 'Gauge' },
  // 下面四组从既有业务系统搬入，当前只有入口没有功能。
  // 放在监控之后、系统管理之前：核心链路（数据→模型→训练→评测→调试→部署→监控）
  // 是每天都在走的，不该被一批空壳打断；系统管理仍留在最底。
  { name: '应用', icon: 'LayoutGrid' },
  { name: '开发', icon: 'Workflow' },
  { name: '知识', icon: 'BookOpen' },
  { name: '开放', icon: 'Plug' },
  { name: '系统管理', icon: 'Settings' },
  { name: '语音', icon: 'AudioLines' },
]

export const routes: RouteRecordRaw[] = [
  // ── 无布局 ────────────────────────────────────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: {
      title: '登录',
      publicRoute: true,
      menu: false,
    } satisfies MtpRouteMeta,
  },
  {
    path: '/',
    name: 'root',
    redirect: { name: 'overview' },
  },
  {
    // 免登录的智能体对话页（契约 §26.2）。share_id 本身即凭据，
    // 链接被重置或关闭后后端一律 404，页面据此显示「链接已失效」。
    path: '/share/agent/:shareId',
    name: 'shared-agent-chat',
    component: () => import('@/views/share/SharedAgentChatView.vue'),
    meta: {
      title: '智能体对话',
      publicRoute: true,
      menu: false,
      // 标题由页面写成智能体名字。这是给患者看的页面，「· 品牌 · 训推一体化平台」
      // 那串后缀对他们没有意义（台账 AG-159）。
      ownTitle: true,
    } satisfies MtpRouteMeta,
  },

  {
    // 免登录的数字人访客页（VOICE-87 计划 C）。linkKey 本身即凭据。
    // 链接关闭、重置、数字人停用或被删，后端一律 404，页面统一显示「链接不可用」。
    path: '/share/digital-human/:linkKey',
    name: 'shared-digital-human',
    component: () => import('@/views/share/DigitalHumanVisitorView.vue'),
    meta: {
      title: '数字人',
      publicRoute: true,
      menu: false,
      // 标题写成数字人的名字：这是给患者看的页面，品牌后缀对他们没有意义
      // （与分享页 AG-159 同一条理由）。
      ownTitle: true,
    } satisfies MtpRouteMeta,
  },

  {
    // 免登录的对接文档页（台账 OA-20）。key 本身即凭据，页面只用它查一次清单，
    // 不落盘。把这个链接发给对接方，他粘上自己那把 key 就能看到能调什么、
    // 参数叫什么 —— 不必为此给他开一个平台账号。
    path: '/api-doc',
    name: 'api-doc',
    component: () => import('@/views/open/ApiDocView.vue'),
    meta: {
      title: '接口对接说明',
      publicRoute: true,
      menu: false,
    } satisfies MtpRouteMeta,
  },

  // ── 主布局 ────────────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/views/overview/OverviewView.vue'),
        meta: {
          title: '系统概览',
          perms: ['system:read'],
          menu: true,
          icon: 'LayoutDashboard',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },

      // ── 服务器（契约 §22：一期就是母机/子机）────────────
      {
        path: 'servers',
        name: 'server-list',
        component: () => import('@/views/server/ServerListView.vue'),
        meta: {
          title: '服务器',
          perms: ['system:read'],
          menu: true,
          icon: 'Monitor',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'servers/create',
        name: 'server-create',
        component: () => import('@/views/server/ServerCreateView.vue'),
        meta: {
          title: '纳管服务器',
          perms: ['server:manage'],
          parent: 'server-list',
        },
      },
      {
        path: 'servers/:id',
        name: 'server-detail',
        component: () => import('@/views/server/ServerDetailView.vue'),
        meta: {
          title: '服务器详情',
          perms: ['system:read'],
          parent: 'server-list',
        },
      },

      // ── 语音（BD-16）────────────────────────────────────
      {
        path: 'speech/settings',
        name: 'speech-settings',
        component: () => import('@/views/speech/SpeechSettingsView.vue'),
        meta: {
          title: '语音模型',
          perms: ['speech:use'],
          menu: true,
          group: '语音',
          icon: 'AudioLines',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'speech/voiceprints',
        name: 'speech-voiceprints',
        component: () => import('@/views/speech/VoiceprintView.vue'),
        meta: {
          title: '声纹库',
          perms: ['voiceprint:manage'],
          menu: true,
          group: '语音',
          icon: 'AudioLines',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'speech/assistant',
        name: 'speech-assistant',
        component: () => import('@/views/speech/MedicalAssistantView.vue'),
        meta: {
          title: 'AI 医助',
          perms: ['speech:use'],
          menu: true,
          group: '语音',
          icon: 'AudioLines',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'speech/voice-profiles',
        name: 'speech-voice-profiles',
        component: () => import('@/views/speech/VoiceProfileView.vue'),
        meta: {
          title: '声音档案',
          perms: ['speech:use'],
          menu: true,
          group: '语音',
          icon: 'AudioWaveform',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      // 文稿朗读并进声音档案页的「朗读」页签；旧链接带原 query 落到那个页签。
      {
        path: 'speech/read-aloud',
        name: 'speech-read-aloud',
        redirect: (to) => ({ name: 'speech-voice-profiles', query: { ...to.query, tab: 'read' } }),
      },
      {
        path: 'speech/avatar-profiles',
        name: 'speech-avatar-profiles',
        component: () => import('@/views/speech/AvatarProfileView.vue'),
        meta: {
          title: '形象库',
          perms: ['speech:use'],
          menu: true,
          group: '语音',
          icon: 'Users',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'speech/digital-humans',
        name: 'speech-digital-humans',
        component: () => import('@/views/speech/DigitalHumanView.vue'),
        meta: {
          title: '数字人',
          perms: ['speech:use'],
          menu: true,
          group: '语音',
          icon: 'Bot',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },

      // ── 数据管理（E2E A1）────────────────────────────────
      {
        path: 'data/masters',
        name: 'dataset-masters',
        component: () => import('@/views/dataset/MasterListView.vue'),
        meta: {
          title: '数据集',
          perms: ['dataset:read'],
          menu: true,
          group: '数据管理',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'data/connections',
        name: 'data-connections',
        component: () => import('@/views/data/DbConnectionListView.vue'),
        meta: {
          // 侧栏渲染的是 `admin_menu` 表（本项挂在 `0001` 数据管理下）。本文件的
          // `group` 只在 `/menus/me` 整体拉不到时才生效（`stores/menu.ts` 的 `failed`），
          // **不逐条兜底**：库里少这一行，侧栏就少这一项，这里写了也补不回来。
          title: '数据连接',
          perms: ['dbconn:read'],
          menu: true,
          group: '数据管理',
        } satisfies MtpRouteMeta,
      },
      {
        path: 'data/connections/:id/browse',
        name: 'db-browse',
        component: () => import('@/views/data/DbBrowseView.vue'),
        meta: {
          title: '浏览数据库',
          perms: ['dbconn:read'],
          parent: 'data-connections',
        },
      },
      {
        path: 'data/masters/:id',
        name: 'dataset-master-detail',
        component: () => import('@/views/dataset/MasterDetailView.vue'),
        meta: {
          title: '母版详情',
          perms: ['dataset:read'],
          parent: 'dataset-masters',
        },
      },
      {
        path: 'data/masters/:id/convert',
        name: 'dataset-convert',
        component: () => import('@/views/dataset/ConvertView.vue'),
        meta: {
          title: '生成子版本',
          perms: ['dataset:create'],
          parent: 'dataset-masters',
        },
      },
      {
        path: 'data/versions/:id',
        name: 'dataset-version',
        component: () => import('@/views/dataset/VersionDetailView.vue'),
        meta: {
          title: '子版本详情',
          perms: ['dataset:read'],
          parent: 'dataset-masters',
        },
      },
      {
        path: 'data/versions/:id/edit',
        name: 'dataset-version-edit',
        component: () => import('@/views/dataset/VersionEditorView.vue'),
        meta: {
          title: '逐条编辑',
          perms: ['dataset:edit_rows'],
          parent: 'dataset-masters',
        },
      },
      {
        path: 'data/quality/:versionId',
        name: 'dataset-quality',
        component: () => import('@/views/dataset/QualityReportView.vue'),
        meta: {
          title: '质量报告',
          perms: ['dataset:read'],
          parent: 'dataset-masters',
        },
      },

      // ── 模型管理（E2E B1/B2）────────────────────────────
      {
        path: 'models',
        name: 'model-list',
        component: () => import('@/views/model/ModelListView.vue'),
        meta: {
          title: '模型仓库',
          perms: ['model:read'],
          menu: true,
          group: '模型管理',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      // ★★ 「下载与导入」**不再是独立菜单项**（台账 M-07，用户要求过两次）。
      //
      // > 2026-08-04「这个不用新开页面独立菜单，应该是作为模型仓库里面，
      // >  点了上传后，一个 75% 宽度抽屉显示就行了」
      //
      // 路由本身保留成一条**重定向**：旧链接（收藏夹、文档、别人发的 URL）
      // 还得能用，而它现在落到模型仓库并把抽屉打开。
      // 直接删掉路由的代价是那些链接变成 404，而 404 看起来像功能被删了。
      {
        path: 'models/import',
        name: 'model-import',
        redirect: { name: 'model-list', query: { import: '1' } },
      },
      // 合并 / 量化同样不再是独立菜单（台账 M-15）：表单是模型仓库里的 75% 抽屉。
      // 路由保留成重定向并把 query 原样带过去 —— 任务详情页「拿它去合并」
      // 与收藏夹里的旧链接都按 name + query 跳，删掉路由它们就成了 404。
      {
        path: 'models/merge',
        name: 'job-create-merge',
        redirect: (to) => ({ name: 'model-list', query: { ...to.query, merge: '1' } }),
      },
      {
        path: 'models/quantize',
        name: 'job-create-quantize',
        redirect: (to) => ({ name: 'model-list', query: { ...to.query, quantize: '1' } }),
      },
      {
        path: 'models/:id',
        name: 'model-detail',
        component: () => import('@/views/model/ModelDetailView.vue'),
        meta: {
          title: '模型详情',
          perms: ['model:read'],
          parent: 'model-list',
        },
      },

      // ── 训练任务（E2E A2/A3）────────────────────────────
      {
        path: 'jobs',
        name: 'job-list',
        component: () => import('@/views/job/JobListView.vue'),
        meta: {
          title: '任务列表',
          perms: ['job:read'],
          menu: true,
          group: '训练任务',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'jobs/create/train',
        name: 'job-create-train',
        component: () => import('@/views/job/create/TrainCreateView.vue'),
        meta: {
          title: '创建训练任务',
          perms: ['job:create'],
          menu: true,
          group: '训练任务',
        },
      },
      {
        path: 'templates',
        name: 'template-list',
        component: () => import('@/views/template/TemplateListView.vue'),
        meta: {
          title: '训练模板',
          perms: ['template:read'],
          menu: true,
          group: '训练任务',
        },
      },
      {
        // 实时看板。内部用 Tab 切「看板 / 参数 / 产物 / 全屏日志」，
        // **不再开新路由**——避免同一 job 的 SSE 在路由切换间反复建连。
        path: 'jobs/:id',
        name: 'job-detail',
        component: () => import('@/views/job/JobDetailView.vue'),
        meta: { title: '任务详情', perms: ['job:read'], parent: 'job-list' },
      },

      // ── 评测分析 ────────────────────────────────────────
      {
        path: 'evaluations',
        name: 'eval-list',
        component: () => import('@/views/eval/EvalListView.vue'),
        meta: {
          title: '评测与预测',
          perms: ['job:read'],
          menu: true,
          group: '评测分析',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        // 建评测是**评测列表里的抽屉**（`EvalCreateDrawer`），不是独立菜单页 ——
        // 与新建部署同一条约定。路由保留是为了深链（`?create=1` 也能开抽屉），
        // 但 `menu: false`：侧栏在评测这一片只留对象列表。
        //
        // ★ 侧栏读的是 `admin_menu` 表而不是这里的 `menu`（M-07 实测），
        //   所以同批还有 `deploy/sql/2026-08-09-eval-menu-collapse.sql`。
        path: 'evaluations/create',
        name: 'job-create-eval',
        component: () => import('@/views/job/create/EvalCreateView.vue'),
        meta: {
          title: '新建评测',
          perms: ['job:create'],
          menu: false,
          group: '评测分析',
        },
      },
      {
        path: 'evaluations/compare',
        name: 'eval-compare',
        component: () => import('@/views/eval/EvalCompareView.vue'),
        meta: {
          title: '结果对比',
          perms: ['job:read'],
          // 侧栏入口 0024/0063 在 2026-08-12 随工作台改造删掉了（对比改成列表页勾选驱动），
          // 但这一位当时没跟着翻。08-26 上线的菜单守卫会把「menu 为 true 却不在下发菜单树里」
          // 的路由送去 403 ⇒ 非超管点工作台那颗「对比」直接吃 403。翻成 false：
          // 路由本身保留（已发出去的直链不该 404），它只是不再要求侧栏里有对应菜单项。
          menu: false,
          group: '评测分析',
        },
      },
      {
        // 侧栏那一项**不是**由这里的 `menu: true` 生成的：侧栏读 `admin_menu` 表
        // （见 `deploy/sql/2026-08-11-eval-session-menu.sql`）。这里的 `menu` 只影响
        // 路由自身的元数据与面包屑。
        path: 'evaluations/rubrics',
        name: 'eval-rubrics',
        component: () => import('@/views/eval/RubricListView.vue'),
        meta: {
          title: '评分卡',
          perms: ['job:read'],
          menu: true,
          group: '评测分析',
        },
      },
      {
        // 题库（16 章 §3.1）。侧栏那一项同样来自 `admin_menu` 表而不是这里的
        // `menu: true` —— 同批给了 `deploy/sql/2026-08-10-exam-bank-menu.sql`。
        path: 'evaluations/exam-banks',
        name: 'exam-banks',
        component: () => import('@/views/eval/ExamBankListView.vue'),
        meta: {
          title: '题库',
          perms: ['job:read'],
          menu: true,
          group: '评测分析',
        },
      },
      {
        // 题库详情：一道题的题面、维度雷达与历史考情（时间倒序）。
        // 侧栏没有它 —— 入口是题库页「题目」页签的行点击；`parent` 指回题库页。
        path: 'evaluations/exam-banks/:bankId/items/:itemId',
        name: 'exam-item-detail',
        component: () => import('@/views/eval/review/ExamItemDetailView.vue'),
        meta: { title: '题目详情', perms: ['job:read'], parent: 'exam-banks' },
      },
      {
        // 多份判官报告并排。选中集合走 query（`?runs=R1,R2`）——
        // 这一屏的结论是要发给别人看的，不带选择的链接分享出去是一张空白页。
        path: 'evaluations/judge-compare',
        name: 'judge-compare',
        component: () => import('@/views/eval/JudgeCompareView.vue'),
        meta: {
          title: '判官报告对比',
          perms: ['job:read'],
          // 侧栏入口 0024/0063 在 2026-08-12 随工作台改造删掉了（对比改成列表页勾选驱动），
          // 但这一位当时没跟着翻。08-26 上线的菜单守卫会把「menu 为 true 却不在下发菜单树里」
          // 的路由送去 403 ⇒ 非超管点工作台那颗「对比」直接吃 403。翻成 false：
          // 路由本身保留（已发出去的直链不该 404），它只是不再要求侧栏里有对应菜单项。
          menu: false,
          group: '评测分析',
        },
      },
      {
        // 发起一轮打分制评测（U-16 批 D′）。批 C 把接口和结果页都做完了，
        // 而**没有任何页面能发起它** —— 只有 API 没有入口在本仓的判定里等于没做完。
        //
        // ★ 路径刻意**不是** `evaluations/judge-runs/new`：它会与下面那条
        //   `judge-runs/:runId` 争同一段路径。vue-router 的静态段确实排在参数段
        //   之前，但那是一条要靠记忆维护的隐式规则 —— 换成独立路径之后，
        //   「新建」与「某一次结果」在 URL 上就永远不会互相遮蔽。
        //
        // ★ 侧栏那一项同样来自 `admin_menu`（见批 C 的注释），
        //   同批的 SQL 是 `deploy/sql/2026-08-05-judge-launch-menu.sql`。
        path: 'evaluations/judge-launch',
        name: 'judge-run-create',
        component: () => import('@/views/eval/JudgeRunCreateView.vue'),
        meta: {
          title: '发起打分评测',
          // 这个动作会真的花钱或真的占显卡 —— 与后端 `create_scored_run` 的
          // `JOB_CREATE` 同界，不是 `job:read`。
          perms: ['job:create'],
          menu: false,
          group: '评测分析',
        },
      },
      {
        // 打分制判官的结果页。侧栏没有它 —— 它是一次具体结论的详情，入口在
        // 评分卡页与判官面板里；`parent` 指回评分卡列表，面包屑才不会断。
        path: 'evaluations/judge-runs/:runId',
        name: 'judge-run-detail',
        component: () => import('@/views/eval/JudgeRunDetailView.vue'),
        meta: { title: '判官打分结果', perms: ['job:read'], parent: 'eval-rubrics' },
      },
      {
        // 场次详情：跑的时候是进度页，跑完是报告页 —— 同一个 URL（设计 18 §5.2）。
        // 侧栏没有它，入口在评测列表页的场次区块；`parent` 指回列表页，面包屑才不会断。
        //
        // 路径排在 `evaluations/:id` **之前**：`sessions` 是静态段，vue-router 会
        // 先匹配它，但把两条的相对顺序写清楚可以省掉一次「为什么点进去是另一个页面」。
        path: 'evaluations/sessions/:sessionId',
        name: 'eval-session-detail',
        component: () => import('@/views/eval/EvalSessionDetailView.vue'),
        meta: { title: '评测场次', perms: ['job:read'], parent: 'eval-list' },
      },
      {
        // 单题复核台：一场评测里逐题看判定并落人工复核（设计 18 §5.4，契约 §32）。
        // 侧栏没有它 —— 入口是场次详情的逐题表；`parent` 指回评测列表。
        path: 'evaluations/sessions/:sessionId/review/:itemNo',
        name: 'eval-item-review',
        component: () => import('@/views/eval/review/EvalItemReviewView.vue'),
        meta: { title: '单题复核', perms: ['job:read'], parent: 'eval-list' },
      },
      {
        path: 'evaluations/:id',
        name: 'eval-detail',
        component: () => import('@/views/eval/EvalDetailView.vue'),
        meta: { title: '评测详情', perms: ['job:read'], parent: 'eval-list' },
      },
      {
        // 参数是 `:reportId` 而不是 `:jobId`：08 章 §8.6 的明细端点是
        // `GET /api/v1/eval-reports/{report_id}/samples`，用 report id 直连少一次反查。
        path: 'predictions/:reportId',
        name: 'predict-detail',
        component: () => import('@/views/eval/PredictDetailView.vue'),
        meta: { title: '预测明细', perms: ['job:read'], parent: 'eval-list' },
      },

      // ── 模型调试（E2E A4 的载体）────────────────────────
      {
        path: 'playground',
        name: 'playground',
        component: () => import('@/views/playground/PlaygroundView.vue'),
        meta: {
          title: '模型调试',
          perms: ['instance:create'],
          menu: true,
          icon: 'MessageCircle',
        } satisfies MtpRouteMeta,
      },

      // ── 部署与服务（契约 §25，E2E D1-D4）────────────────
      {
        path: 'deployments',
        name: 'deployment-list',
        component: () => import('@/views/deploy/DeploymentListView.vue'),
        meta: {
          title: '部署列表',
          perms: ['instance:read'],
          menu: true,
          group: '部署与服务',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'deployments/create',
        name: 'deployment-create',
        component: () => import('@/views/deploy/DeploymentCreateView.vue'),
        meta: {
          // 不进菜单：入口是部署列表里的抽屉，路由保留只为兼容旧链接。
          title: '新建部署',
          perms: ['instance:persist'],
          menu: false,
          group: '部署与服务',
        },
      },
      {
        path: 'api-keys',
        name: 'api-keys',
        component: () => import('@/views/deploy/ApiKeyListView.vue'),
        meta: {
          title: 'API Key',
          perms: ['apikey:manage'],
          menu: true,
          group: '部署与服务',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        // 从 API Key 页勾选后跳过来。**不进菜单树**（`admin_menu` 里没有这一行），
        // 但仍是独立路由：选中集合走 query，刷新还在、链接能发给别人、面包屑回得去。
        //
        // `menu: false` 与 `parent` 缺一不可（`router/guards.ts:67-80`）：
        // 前者不写的话非超管直链会被判 `forbidden`（菜单表里查不到这个 route_name），
        // 而且 `/menus/me` 拉不到时降级推导会让它突然出现在侧栏；
        // 后者让「看不到 API Key 页的人也进不来」，同时给出面包屑的返回路径。
        path: 'api-keys/compare',
        name: 'api-key-compare',
        component: () => import('@/views/deploy/ApiKeyCompareView.vue'),
        meta: {
          title: '用量对比',
          perms: ['apilog:read'],
          menu: false,
          parent: 'api-keys',
          group: '部署与服务',
        } satisfies MtpRouteMeta,
      },
      {
        path: 'api-logs',
        name: 'api-logs',
        component: () => import('@/views/deploy/ApiLogListView.vue'),
        meta: {
          title: '调用日志',
          perms: ['apilog:read'],
          menu: true,
          group: '部署与服务',
          keepAlive: true,
        } satisfies MtpRouteMeta,
      },
      {
        path: 'deployments/:id',
        name: 'deployment-detail',
        component: () => import('@/views/deploy/DeploymentDetailView.vue'),
        meta: {
          title: '部署详情',
          perms: ['instance:read'],
          parent: 'deployment-list',
        },
      },
      {
        path: 'api-logs/:id',
        name: 'api-log-detail',
        component: () => import('@/views/deploy/ApiLogDetailView.vue'),
        meta: { title: '调用详情', perms: ['apilog:read'], parent: 'api-logs' },
      },

      // ── 监控 ────────────────────────────────────────────
      {
        path: 'monitor',
        name: 'gpu-monitor',
        component: () => import('@/views/monitor/GpuMonitorView.vue'),
        meta: {
          title: '实时监控',
          perms: ['gpu:read'],
          menu: true,
          group: '监控',
        },
      },
      {
        path: 'monitor/queue',
        name: 'queue-monitor',
        component: () => import('@/views/monitor/QueueView.vue'),
        meta: {
          title: '调度队列',
          perms: ['job:read'],
          menu: true,
          group: '监控',
        },
      },
      {
        path: 'monitor/compute-center',
        name: 'compute-center',
        component: () => import('@/views/monitor/ComputeCenterView.vue'),
        meta: {
          title: '智算中心',
          perms: ['gpu:read'],
          menu: true,
          group: '监控',
        },
      },
      // 租户侧的只读机器视图。「服务器」整页归平台超管之后，租户仍要知道
      // 自己那几张卡在哪台机器上（契约 §22.10）。
      {
        path: 'monitor/my-machines',
        name: 'my-machines',
        component: () => import('@/views/server/MyMachinesView.vue'),
        meta: {
          title: '我的机器',
          perms: ['gpu:read'],
          menu: true,
          group: '监控',
        },
      },

      // ── 应用 ────────────────────────────────────────────
      // 「智能体即应用」（不建 apps 壳表，智能体本身就是应用）。
      // 对话 = 与已发布智能体的落库会话（AG-25 第 2 步）；我的应用 = 使用侧
      // 列表（AG-29 第 1 步）；应用市场是同一份正文的另一种模式（AG-123）。
      //
      // 市场那条不写 `perms`：硬填一个不存在的码会让 `permission.ts` 的
      // `has()` 永远返回 false —— 菜单**静默消失**。已实现的两页用
      // `agent:read`（后端同名权限码已存在，会话写操作由后端 `agent:manage` 拦）。
      {
        path: 'app/conversation',
        name: 'app-conversation',
        component: () => import('@/views/app/ConversationView.vue'),
        // 不是全屏路由：它是常驻应用，平台菜单要一直在（用户 2026-09-08 裁决走
        // 「窄屏收窄前两列」而不是「让出侧栏」）。1500px 以下前两列收窄，
        // 判据与做法见 `ConversationView.vue` 里那条 `min-[1500px]` 注释。
        meta: { title: '对话', perms: ['agent:read'], menu: true, group: '应用' },
      },
      {
        path: 'app/mine',
        name: 'app-mine',
        component: () => import('@/views/app/MyAppListView.vue'),
        meta: { title: '我的应用', perms: ['agent:read'], menu: true, group: '应用' },
      },
      {
        path: 'app/market',
        name: 'app-market',
        component: () => import('@/views/app/AppMarketView.vue'),
        meta: { title: '应用市场', menu: true, group: '应用' },
      },

      // ── 开发 ────────────────────────────────────────────
      {
        // 编排工作台（设计 §7.1）。**不进菜单** —— 它是「某一个智能体」的详情页，
        // 没有 agentId 就打不开，放进侧栏等于给一个点了会报错的入口。
        // 与工作流画布同一形态（AG-43）：全屏工作区，平台菜单不渲染，
        // 返回、身份与页内导航都由页面自己的左侧栏承担。
        path: 'dev/agents/:agentId',
        name: 'dev-agent-detail',
        component: () => import('@/views/dev/AgentDetailView.vue'),
        meta: { title: '编排', perms: ['agent:read'], fullscreen: true },
      },
      {
        path: 'dev/workflows',
        name: 'dev-workflows',
        component: () => import('@/views/dev/WorkflowListView.vue'),
        meta: { title: '工作流', menu: true, group: '开发' },
      },
      {
        // 画布。**不进菜单** —— 它是「某一条工作流」的详情页，没有 workflowId 就
        // 打不开，放进侧栏等于给一个点了会报错的入口（与 `dev/agents/:agentId` 同）。
        //
        // 画布必须带 `workflowId`，不给无 id 的入口 —— 一个存不进任何地方的画布
        //   就是「点了没反应」。
        path: 'dev/workflows/:workflowId',
        name: 'dev-workflow-detail',
        component: () => import('@/views/dev/WorkflowDetailView.vue'),
        meta: { title: '工作流画布', perms: ['agent:read'], fullscreen: true },
      },
      {
        path: 'dev/agents',
        name: 'dev-agents',
        component: () => import('@/views/dev/AgentListView.vue'),
        meta: { title: '智能体', menu: true, group: '开发' },
      },

      // ── 知识 ────────────────────────────────────────────
      {
        path: 'knowledge/bases',
        name: 'knowledge-bases',
        component: () => import('@/views/knowledge/KnowledgeBaseListView.vue'),
        // `perms` 现在能写了：后端 `app/core/permissions.py` 已经有 `kb:*` 十一个值，
        // `routes.spec.ts` 那条「meta.perms 都是后端真实存在的权限码」会过。
        meta: { title: '知识库', perms: ['kb:read'], menu: true, group: '知识' },
      },
      {
        path: 'knowledge/bases/:kbId',
        name: 'knowledge-base-detail',
        component: () => import('@/views/knowledge/KnowledgeBaseDetailView.vue'),
        meta: { title: '知识库详情', perms: ['kb:read'], parent: 'knowledge-bases', menu: false },
      },
      {
        // 上传是**独立页面**而不是详情页里的弹窗（同「分块管理」的理由）：
        // 一次上传要同时摆下文件清单、五档切分方式、FAQ 的列选择器与分段预览，
        // 塞进 58rem 的对话框后选列那张表只剩 427px，四列就得横着滚。
        path: 'knowledge/bases/:kbId/upload',
        name: 'knowledge-doc-upload',
        component: () => import('@/views/knowledge/KnowledgeUploadView.vue'),
        meta: {
          title: '上传文档',
          perms: ['kb:doc_write'],
          parent: 'knowledge-bases',
          menu: false,
        },
      },
      {
        path: 'knowledge/bases/:kbId/eval',
        name: 'knowledge-eval',
        component: () => import('@/views/knowledge/KnowledgeEvalView.vue'),
        meta: { title: '评测与回归', perms: ['kb:read'], parent: 'knowledge-bases', menu: false },
      },
      {
        // 分块是**独立页面**而不是详情页里的弹窗：正文上限 20000 字符，
        // 在一个 32rem 的对话框里改长文本是受罪；而且分块要分页，弹窗里套分页更绕。
        path: 'knowledge/bases/:kbId/docs/:docId/chunks',
        name: 'knowledge-doc-chunks',
        component: () => import('@/views/knowledge/KnowledgeChunkListView.vue'),
        meta: {
          title: '分块管理',
          perms: ['kb:read'],
          parent: 'knowledge-bases',
          menu: false,
        },
      },

      // ── 开放 ────────────────────────────────────────────
      {
        path: 'open/mcp',
        name: 'open-mcp',
        component: () => import('@/views/open/McpMarketView.vue'),
        meta: { title: 'MCP 连接', menu: true, group: '开放' },
      },
      {
        path: 'open/model-endpoints',
        name: 'open-model-endpoints',
        component: () => import('@/views/open/ModelEndpointListView.vue'),
        // 侧栏渲染的是 `admin_menu` 表（本项挂在 `0008` 开发下）。这里的 `group`
        // 只在 `/menus/me` 整体拉不到时才生效，**不逐条兜底**：库里少这一行，
        // 侧栏就少这一项。（MCP 连接那条写着「开放」，与库不一致，是既有欠账。）
        meta: { title: '模型端点', perms: ['mcp:read'], menu: true, group: '开发' },
      },
      {
        path: 'open/benchmarks',
        name: 'open-benchmarks',
        component: () => import('@/views/open/BenchmarkListView.vue'),
        // `group` 与库一致（`admin_menu` 0076 挂在 0005「部署与服务」下，紧跟「模型端点」）。
        // 它只在 `/menus/me` 整体拉不到时才生效，不逐条兜底。
        meta: { title: '端点压测', perms: ['bench:read'], menu: true, group: '部署与服务' },
      },
      {
        path: 'open/benchmarks/:runId',
        name: 'open-benchmark-report',
        component: () => import('@/views/open/BenchmarkReportView.vue'),
        // 详情页不进菜单：它从列表点进来，侧栏多一项「某一次压测」没有意义。
        meta: { title: '压测报告', perms: ['bench:read'] },
      },

      // ── 系统管理 ────────────────────────────────────────
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/UserListView.vue'),
        meta: {
          title: '用户与角色',
          perms: ['member:read'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/tenants',
        name: 'admin-tenants',
        component: () => import('@/views/admin/TenantListView.vue'),
        meta: {
          title: '租户',
          perms: ['tenant:create'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/quotas',
        name: 'admin-quotas',
        component: () => import('@/views/admin/QuotaView.vue'),
        meta: {
          title: '资源配额',
          perms: ['quota:read'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/audit',
        name: 'admin-audit',
        component: () => import('@/views/admin/AuditLogView.vue'),
        meta: {
          title: '审计日志',
          perms: ['audit:read'],
          menu: true,
          group: '系统管理',
        },
      },
      // 设计 14 §7.2.1 那个「统一日志页」的位置，与「审计日志」同组。
      // 权限跟着数据源走（后端 `GET /agent-runs` 要 `agent:read`），
      // 不用 `audit:read` —— 那是另一套数据的门。
      //
      // 侧栏入口不由这里产生：它读 `admin_menu` 表，见
      // `deploy/sql/2026-08-11-menu-orphan-0061-reparent.sql`。
      {
        path: 'admin/agent-runs',
        name: 'admin-agent-runs',
        component: () => import('@/views/admin/AgentRunLogView.vue'),
        meta: {
          // 与库里的 `0011` 保持一致：不一致时整体降级那一刻这一项会跳到别的分组。
          title: '运行日志',
          perms: ['agent:read'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/SystemSettingsView.vue'),
        meta: {
          title: '系统设置',
          perms: ['maintenance:run'],
          menu: true,
          group: '系统管理',
        },
      },

      // 以下为搬入的系统管理项，功能尚未接入。
      //
      // 「用户与角色」保持原样不拆 —— 现有菜单一条不动。所以这里只补一个
      // 独立的「角色管理」入口，将来接的是角色本身的增删与权限编排；
      // 「给某个人换角色」仍留在「用户与角色」里。
      //
      // 权限码只用后端 `core/permissions.py` 里真实存在的；没有对应码的
      // 一律留空（登录即可见），不硬填 —— 填一个不存在的码会让菜单静默消失。
      {
        path: 'admin/roles',
        name: 'admin-roles',
        component: () => import('@/views/admin/RoleListView.vue'),
        meta: {
          title: '角色管理',
          perms: ['maintenance:run'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/menus',
        name: 'admin-menus',
        component: () => import('@/views/admin/MenuListView.vue'),
        meta: {
          title: '菜单管理',
          perms: ['maintenance:run'],
          menu: true,
          group: '系统管理',
        },
      },
      // 参数配置（PARAM-CONFIG-PAGE）：平台运行时的时间、次数与阈值。
      // 菜单行 `0066` 的 `superadmin_only=true`，非超管连入口都拿不到；
      // 接口层另有 `require_superadmin` 兜写侧。
      {
        path: 'admin/params',
        name: 'admin-params',
        component: () => import('@/views/admin/ParamConfigView.vue'),
        meta: {
          title: '参数配置',
          perms: ['maintenance:run'],
          menu: true,
          group: '系统管理',
        },
      },
      // 平台统计（STATS-ALL-TENANTS-PAGE）：全平台口径的那一页。
      // 与「Token 用量」「智算中心」不是同一件事 —— 那些按租户看，这一页不切租户，
      // 也不发任何作用域参数。菜单行 `0071` 的 `superadmin_only=true` 挡入口，
      // 接口层 `require_superadmin` 挡数据，两道独立。
      {
        path: 'admin/platform-stats',
        name: 'admin-platform-stats',
        component: () => import('@/views/admin/PlatformStatsView.vue'),
        meta: {
          title: '平台统计',
          perms: ['maintenance:run'],
          menu: true,
          group: '系统管理',
        },
      },
      {
        path: 'admin/token-calc',
        name: 'admin-token-calc',
        component: () => import('@/views/admin/TokenCalcView.vue'),
        meta: { title: 'Token 用量', perms: ['apilog:read'], menu: true, group: '系统管理' },
      },

      // ── 个人 ────────────────────────────────────────────
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/user/ProfileView.vue'),
        meta: { title: '个人设置' },
      },
    ],
  },

  // ── 错误页 ────────────────────────────────────────────────
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/error/ForbiddenView.vue'),
    meta: { title: '无权访问', publicRoute: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: { title: '页面不存在', publicRoute: true },
  },
]
